import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, ElementRef, LOCALE_ID, OnDestroy, OnInit, PLATFORM_ID, QueryList, ViewChild, ViewChildren, inject, viewChild } from '@angular/core';
import { CommonModule, formatNumber, isPlatformBrowser } from '@angular/common';
import { FormControl, FormGroup, FormRecord, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Location, LocationFinderService, LocationType, LocationFinderSortField, PhysicalLocation, BaseLocation } from '../../../app/providers/location-finder.service';
import { Section, SectionAttributes } from '../../../app/types/section';
import { ActivatedRoute, Router } from '@angular/router';
import { EMPTY, debounceTime, startWith, tap, Observable, ReplaySubject, combineLatest, filter, BehaviorSubject, shareReplay, map, firstValueFrom, distinctUntilChanged, Subject, merge } from 'rxjs';
import { CdkVirtualScrollViewport, ScrollingModule } from '@angular/cdk/scrolling';
import { AsPipe } from '../../../app/providers/as.pipe';
import { AddressComponent } from '../../../app/components/address/address.component';
import { preventScroll } from '../../providers/page.service';
import { MarketingConsentCategory } from '../../../app/providers/one-trust.service';
import { UserConsentService } from '../../../app/providers/user-consent.service';
import { Cluster, MarkerClusterer, SuperClusterAlgorithm } from '@googlemaps/markerclusterer';
import { ContactDetailsComponent } from '../../../app/components/contact-details/contact-details.component';
import { ContactComponent } from '../../../app/components/contact/contact.component';
import { GoogleMapsService } from '../../../app/providers/google-maps.service';
import { COUNTRY_COORDINATES } from './country-coordinates';
import { environment } from '../../../environments/environment';
import { EditorIdDirective } from '../../../app/directives/editor-id.directive';
import { CountryNamePipe } from '../../providers/country-name.pipe';
import { UserConsentDialogComponent } from '../../../app/components/user-consent-dialog/user-consent-dialog.component';
import { SectionComponent } from '../../../app/components/section/section.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { iconArrowsRight, iconCheck, iconGpsLocation, iconRoute, iconSearch } from '@knorr-bremse-portals/icons';
import { IconComponent } from '@knorr-bremse-portals/ngx-components/components/icon';
import { SelectComponent, SelectOptionComponent } from '@knorr-bremse-portals/ngx-components/components/controls/select';
import { InputComponent } from '@knorr-bremse-portals/ngx-components/components/controls/input';
import { RangeComponent } from '@knorr-bremse-portals/ngx-components/components/controls/range';
import { CheckboxComponent } from '@knorr-bremse-portals/ngx-components/components/controls/checkbox';
import { ProgressIndicatorComponent } from '@knorr-bremse-portals/ngx-components/components/progress-indicator';
import { CdkAutoSizeVirtualScroll } from './auto-size-virtual-scroll';
import { FsButtonComponent } from '../../components/fs-button/fs-button.component';
import { Loggers } from '../../../express.tokens';
import { getLogger } from '@knorr-bremse-portals/ngx-components';

const enum QueryParam {
  Query = 'query',
  Country = 'country',
  SortField = 'sort-field',
  Categories = 'categories',
  Competencies = 'competencies',
  Radius = 'radius',
}

type LocationFinderSection = Section<
  {
    numColumns: 12;
    headline: string;
    preselectedCategories: string[];
    selectLocationTypes: LocationType[];
    categories: { [categoryName: string]: {
      colorHex: string;
      displayName: string;
    }; };
    networks: { [networkName: string]: {
      colorHex: string;
      displayName: string;
    }; };
    competencies: { [competenceName: string]: {
      displayName: string;
    }; };
  } & SectionAttributes,
  [
    'headline',
    'preselectedCategories',
    'selectLocationTypes',
  ],
  'location-finder'
>;

const DEFAULT_SORT_FIELD = LocationFinderSortField.Name;
const SEARCH_DEBOUNCE_TIME = 800;

const HEADQUARTERS_COORDINATES = { lat: parseFloat('51.678418'), lng: parseFloat('7.809007') };

const SELECTED_ZOOM_LEVEL = 18;
const MAX_ZOOM = 22;
const DEFAULT_ZOOM = 4;

@Component({
  selector: 'app-location-finder',
  standalone: true,
  imports: [
    CommonModule, EditorIdDirective,
    ReactiveFormsModule, FormsModule, ScrollingModule, CdkAutoSizeVirtualScroll,
    AsPipe, AddressComponent, ContactDetailsComponent, ContactComponent, CountryNamePipe, UserConsentDialogComponent,
    IconComponent,
    SelectComponent,
    SelectOptionComponent,
    InputComponent,
    RangeComponent,
    CheckboxComponent,
    ProgressIndicatorComponent,
    FsButtonComponent,
],
  templateUrl: './location-finder.component.html',
  styleUrls: ['./location-finder.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LocationFinderComponent extends SectionComponent<LocationFinderSection> implements OnInit, AfterViewInit, OnDestroy {

  private logger = getLogger(Loggers.Renderer);
  private platformBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  protected iconSearch = iconSearch;
  protected iconRoute = iconRoute;
  protected iconCompetence = iconCheck;
  protected iconLocate = iconGpsLocation;
  protected iconMoreInformation = iconArrowsRight;
  protected useDistanceSearch = false;

  protected castToLocation(location: unknown): location is Location {
    return true
  }

  private cdr = inject(ChangeDetectorRef);

  protected sortFields = [
    {
      value: LocationFinderSortField.Name,
      label: $localize `:Location finder sort by company name@@2655331778710053928:Sort by company name`,
    },
    {
      value: LocationFinderSortField.City,
      label: $localize `:Location finder sort by city name@@3569939808505932278:Sort by city name`,
    },
  ];
  protected distanceSortField = {
    value: LocationFinderSortField.Distance,
    label: $localize `:Location finder sort by distance@@5019815367922669381:Sort by distance`,
  }

  private localeId = inject(LOCALE_ID);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private googleMaps = inject(GoogleMapsService);
  private destroyRef = inject(DestroyRef);

  protected googleMapsConsent = inject(UserConsentService).forCategory(MarketingConsentCategory);

  protected intl = {
    queryLabel: {
      withGoogleMapsConsent: $localize `:Location finder search input label with google maps@@4580988005648117665:Search`,
      withoutGoogleMapsConsent: $localize `:Location finder search input label without google maps@@734344539006968668:Search for company or city`,
    },
  };

  private getDefaultRadius(): number {
    const radius = this.route.snapshot.queryParamMap.get(QueryParam.Radius);
    return radius ? parseInt(radius) : 100;
  }

  private getDefaultFormRecord(queryParamName: string): { [category: string]: FormControl<boolean> } {
    const categories = this.route.snapshot.queryParamMap.getAll(queryParamName);
    const result: { [category: string]: FormControl<boolean> } = {};
    for (const category of categories) {
      result[category] = new FormControl(true, { nonNullable: true });
    }
    return result;
  }

  protected form = new FormGroup({
    query: new FormControl(this.route.snapshot.queryParamMap.get(QueryParam.Query) ?? '', { nonNullable: true }),
    radius: new FormControl<number>(this.getDefaultRadius(), { nonNullable: true }),
    country: new FormControl<string | null>(this.route.snapshot.queryParamMap.get(QueryParam.Country) ?? null),
    sortField: new FormControl<LocationFinderSortField>(this.route.snapshot.queryParamMap.get(QueryParam.SortField) as LocationFinderSortField ?? DEFAULT_SORT_FIELD, { nonNullable: true }),
    categories: new FormRecord<FormControl<boolean>>(this.getDefaultFormRecord(QueryParam.Categories)),
    competencies: new FormRecord<FormControl<boolean>>(this.getDefaultFormRecord(QueryParam.Competencies)),
  });

  private locationFinderService = inject(LocationFinderService);

  protected categories: string[] = [];
  protected competencies: string[] = [];

  protected results$ = this.locationFinderService.results$.pipe(
    tap((result) => {
      if (result.numRows === null) {
        return;
      }

      const categories = result.facets.category.map(facet => facet.value);
      for (const name of Object.keys(this.form.controls.categories.controls)) {
        if (categories.includes(name)) {
          continue;
        }
        this.form.controls.categories.removeControl(name, { emitEvent: false });
      }
      const selected = this.route.snapshot.queryParamMap.getAll(QueryParam.Categories);
      for (const name of categories) {
        if (this.attributes.preselectedCategories.length > 0) {
          if (!this.attributes.preselectedCategories.includes(name)) {
            continue;
          }
        }
        if (this.form.controls.categories.contains(name)) {
          continue;
        }
        this.form.controls.categories.addControl(name, new FormControl(selected.includes(name), { nonNullable: true }), { emitEvent: false });
      }
      this.categories = Object.keys(this.form.controls.categories.controls);
    }),
    tap((result) => {
      if (result.numRows === null) {
        return;
      }

      const competencies = result.facets.competence.map(facet => facet.value);
      for (const name of Object.keys(this.form.controls.competencies.controls)) {
        if (competencies.includes(name)) {
          continue;
        }
        this.form.controls.competencies.removeControl(name, { emitEvent: false });
      }
      const selected = this.route.snapshot.queryParamMap.getAll(QueryParam.Competencies);
      for (const name of competencies) {
        if (this.form.controls.competencies.contains(name)) {
          continue;
        }
        this.form.controls.competencies.addControl(name, new FormControl(selected.includes(name), { nonNullable: true }), { emitEvent: false });
      }
      this.competencies = Object.keys(this.form.controls.competencies.controls);
    }),
    tap((result) => {
      if (result.numRows === null) {
        return;
      }

      const foundLocation = result.locations.find((location) => location.id === this.selectedLocation$.value?.id);
      if (foundLocation) {
        this.selectedLocation$.next(foundLocation);
        return;
      }

      this.selectedLocation$.next(undefined);
    }),
    shareReplay(1),
  );
  protected locations$ = this.results$.pipe(
    map((results) => {
      return results.numRows === null ? null : results.locations;
    }),
  );
  protected facets$ = this.results$.pipe(
    map((results) => {
      return results.numRows === null ? null : results.facets;
    }),
  );
  protected LocationsType?: Location[];
  protected LocationType = LocationType;

  public override ngOnInit(): void {
    super.ngOnInit();
    this.handleQueryParams();
    this.handlePreparedSearchParams();
  }

  public override ngAfterViewInit(): void {
    super.ngAfterViewInit();
    this.handleGoogleMapsInit();
    this.handleVirtualScroller();
  }

  public ngOnDestroy(): void {
    this.googleMapsMap$.complete();
    this.googleMapsBounds$.complete();
    this.selectedLocation$.complete();
    this.filterToVisibleLocations$.complete();
  }

  private getSelectedCategories(): string[] {
    return Object.entries(this.form.controls.categories.value)
        .filter(([, value]) => value === true )
        .map(([key]) => key);
  }

  private getSelectedCompetencies(): string[] {
    return Object.entries(this.form.controls.competencies.value)
        .filter(([, value]) => value === true )
        .map(([key]) => key);
  }

  private async findGeoCoordinates(address: string): Promise<google.maps.GeocoderResult[]> {
    const { Geocoder } = await this.googleMaps.geocoding();
    return new Promise((resolve, reject) => {
      const geocoder = new Geocoder();
      geocoder.geocode({
        address: address,
        region: this.form.controls.country.value,
      }, (results, status) => {
        if (status !== google.maps.GeocoderStatus.OK || results === null) {
          reject(status);
          return;
        }
        resolve(results);
      });
    });
  }

  private async getCurrentPosition(): Promise<GeolocationPosition> {
    return new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(resolve, reject);
    })
  }

  protected async setCurrentPosition(): Promise<void> {
    const position = await this.getCurrentPosition();
    const positionString = `${position.coords.latitude  }, ${  position.coords.longitude}`;
    this.form.controls.query.setValue(positionString);
  }

  private handlePreparedSearchParams(): void {

    if (this.attributes.selectLocationTypes.length > 0) {
      this.locationFinderService.setType(this.attributes.selectLocationTypes);
    }

    merge(
      this.form.controls.country.valueChanges,
      this.form.controls.categories.valueChanges,
      this.form.controls.competencies.valueChanges,
      this.form.controls.sortField.valueChanges,
      this.form.controls.radius.valueChanges,
    ).pipe(
      debounceTime(SEARCH_DEBOUNCE_TIME),
      startWith(EMPTY),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(async () => {
      this.locationFinderService.setCountryCode(this.form.controls.country.value);
      const selectedCategories = this.getSelectedCategories();
      this.locationFinderService.setCategories(selectedCategories.length > 0 ? selectedCategories : this.attributes.preselectedCategories);
      this.locationFinderService.setCompetencies(this.getSelectedCompetencies());
      this.locationFinderService.setSort(this.form.controls.sortField.value);
      this.locationFinderService.setRadius(this.form.controls.radius.value);
    });

    combineLatest([
      this.googleMapsMap$,
      this.form.controls.country.valueChanges.pipe(
        startWith(this.form.controls.country.value),
        filter((countryCode): countryCode is string => countryCode !== null),
      ),
    ]).pipe(
      debounceTime(SEARCH_DEBOUNCE_TIME),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(async ([map, countryCode]) => {
      if (COUNTRY_COORDINATES[countryCode] === undefined) {
        return;
      }
      map.fitBounds(new google.maps.LatLngBounds(COUNTRY_COORDINATES[countryCode].sw, COUNTRY_COORDINATES[countryCode].ne))
    });

    this.form.controls.query.valueChanges.pipe(
      debounceTime(SEARCH_DEBOUNCE_TIME),
      startWith(this.form.controls.query.value),
      distinctUntilChanged(),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(async (query) => {
      if (await firstValueFrom(this.googleMapsConsent.given$) && query) {
        try {
          const results = await this.findGeoCoordinates(query);
          const result = results[0];
          this.locationFinderService.setPos(
            result.geometry.location.toUrlValue(),
            this.form.controls.radius.value,
          );
          this.form.controls.query.setValue(result.formatted_address, {
            emitEvent: false,
          });
          if (this.useDistanceSearch === false) {
            this.useDistanceSearch = true;
            this.cdr.detectChanges();
            this.form.controls.sortField.setValue(LocationFinderSortField.Distance, {
              emitEvent: false,
            });
            this.locationFinderService.setSort(this.form.controls.sortField.value);
          }
          this.updateQueryParams();
          this.locationFinderService.setQuery('');
          return;
        } catch(error) {
          this.logger.error('failed to find geo coordinates', {
            error,
          });
        }
      }

      const coordinates = this.getValidCoordinates(query);
      if (coordinates !== null) {
        if (this.useDistanceSearch === false) {
          this.useDistanceSearch = true;
          this.cdr.detectChanges();
          this.form.controls.sortField.setValue(LocationFinderSortField.Distance, {
            emitEvent: false,
          });
          this.locationFinderService.setSort(this.form.controls.sortField.value);
          this.updateQueryParams();
        }

        this.locationFinderService.setPos(
          coordinates,
          this.form.controls.radius.value,
        );
        this.locationFinderService.setQuery('');
        return;
      }

      if (this.useDistanceSearch === true) {
        if (this.form.controls.sortField.value === LocationFinderSortField.Distance) {
          this.form.controls.sortField.setValue(DEFAULT_SORT_FIELD, {
            emitEvent: false,
          });
          this.locationFinderService.setSort(this.form.controls.sortField.value);
          this.updateQueryParams();
        }
        this.useDistanceSearch = false;
      }

      this.locationFinderService.setQuery(query);
      this.locationFinderService.removePos();
    });

  }

  private getValidCoordinates(coordinate: string): string | null {
    const [latitude, longitude] = coordinate.split(',');
    const lat = parseFloat(latitude);
    const lng = parseFloat(longitude);
    if (isNaN(lat) || isNaN(lng) || lat < -90 || lat > 90 || lng < -180 || lng > 180) {
      return null;
    }
    return [lat, lng].join(',');
  }

  private handleQueryParams(): void {
    this.form.valueChanges.pipe(
      debounceTime(SEARCH_DEBOUNCE_TIME),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => {
      this.updateQueryParams();
    });
  }

  private updateQueryParams(): void {
    const values = this.form.value;
    this.router.navigate([], preventScroll({
      relativeTo: this.route,
      queryParams: {
        [QueryParam.Query]: values.query !== '' ? values.query : undefined,
        [QueryParam.SortField]: values.sortField !== DEFAULT_SORT_FIELD ? values.sortField : undefined,
        [QueryParam.Categories]: this.getSelectedCategories(),
        [QueryParam.Competencies]: this.getSelectedCompetencies(),
        [QueryParam.Country]: values.country,
        [QueryParam.Radius]: values.radius !== 100 ? values.radius : undefined,
      },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    }, this.platformBrowser));
  }

  protected trackByLocation(index: number, item: BaseLocation): string {
    return item.id;
  }

  protected locationTemplateType!: {
    location: BaseLocation;
  }

  protected getRouteLink(location: PhysicalLocation): string {
    return `https://www.google.com/maps/dir/?api=1&destination=${location.latitude},${location.longitude}`;
  }

  private virtualScrollViewport = viewChild.required(CdkVirtualScrollViewport);

  protected selectedLocation$ = new BehaviorSubject<Location | undefined>(undefined);
  protected async selectLocation(location: Location): Promise<void> {
    this.selectedLocation$.next(location);
  }

  private handleVirtualScroller(): void {
    combineLatest([
      this.selectedLocation$.pipe(
        filter((location): location is Location => location !== undefined),
        takeUntilDestroyed(this.destroyRef),
      ),
    ]).subscribe(([location]) => {
      this.virtualScrollViewport().scrollToIndex(location.index, 'smooth');
    });
  }

  @ViewChildren('googleMaps') private googleMapsElements!: QueryList<ElementRef<HTMLDivElement>>;
  @ViewChild('googleMapsFilterControl') private googleMapsFilterControlElement!: ElementRef<HTMLElement>;
  private googleMapsMap$ = new ReplaySubject<google.maps.Map>(1);
  private googleMapsBounds$ = new Subject<google.maps.LatLngBounds | undefined>();
  private handleGoogleMapsInit(): void {
    (this.googleMapsElements.changes as Observable<QueryList<ElementRef<HTMLDivElement>>>).pipe(
      startWith(this.googleMapsElements),
      map(elements => elements.first),
      filter(element => element !== undefined),
    ).subscribe(async (element) => {
      const { Map } = await this.googleMaps.map();
      const map = new Map(element.nativeElement, {
        center: HEADQUARTERS_COORDINATES,
        zoom: DEFAULT_ZOOM,
        mapId: environment.locationFinder.mapId,
      });
      map.controls[google.maps.ControlPosition.LEFT_TOP].push(this.googleMapsFilterControlElement.nativeElement);

      map.addListener('bounds_changed', () => {
        this.googleMapsBounds$.next(map.getBounds());
      });

      this.googleMapsMap$.next(map);
    });

    combineLatest([
      this.googleMapsMap$,
      this.results$.pipe(
        filter(results => results.numRows !== null),
      ),
    ]).pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(([map, results]) => {
      this.updateMarkers(results.locations, map);
    });

    combineLatest([
      this.googleMapsMap$,
      this.selectedLocation$.pipe(
        distinctUntilChanged((location1, location2) => location1?.id === location2?.id),
      ),
    ]).pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(([map, location]) => {
      if (location) {
        map.panTo(new google.maps.LatLng(location.latitude, location.longitude));
        if (map.getZoom() ?? 0 < SELECTED_ZOOM_LEVEL) {
          map.setZoom(SELECTED_ZOOM_LEVEL);
        }
      }
    });

    combineLatest([
      this.googleMapsBounds$,
      this.filterToVisibleLocations$,
    ]).pipe(
      debounceTime(SEARCH_DEBOUNCE_TIME),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(([bounds, filter]) => {
      if (filter === false) {
        this.locationFinderService.setBoundsFilter(undefined);
        return;
      }
      this.locationFinderService.setBoundsFilter(bounds);
    })
  }

  private readonly markerColor = 'rgba(0, 69, 126, .8)';

  private createClusterMarkerElement(label: string): Element {
    const svg = document.createElement('svg');
    svg.innerHTML = `
    <svg fill="${this.markerColor}" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 240 240" width="50" height="50">
      <circle cx="120" cy="120" opacity=".6" r="70" />
      <circle cx="120" cy="120" opacity=".3" r="90" />
      <circle cx="120" cy="120" opacity=".2" r="110" />
      <text x="50%" y="50%" style="fill:#fff" text-anchor="middle" font-size="50" dominant-baseline="middle" font-family="roboto,arial,sans-serif">${label}</text>
    </svg>`;
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    svg.firstElementChild!.setAttribute('transform', 'translate(0 25)');
    return svg;
  }

  private markerCluster?: MarkerClusterer;
  private markerCache = new Map<string, google.maps.marker.AdvancedMarkerElement>();
  private async updateMarkers(locations: Location[], googleMap: google.maps.Map): Promise<void> {

    const {AdvancedMarkerElement, PinElement} = await this.googleMaps.marker();

    for (const location of locations) {
      if (this.markerCache.has(location.id)) {
        continue;
      }

      const latLng = new google.maps.LatLng({
        lat: location.latitude,
        lng: location.longitude,
      });

      const marker = new AdvancedMarkerElement({
        position: latLng,
        content: new PinElement({
          background: this.attributes.categories[location.category].colorHex,
          borderColor: 'none',
          glyphColor: this.attributes.categories[location.category].colorHex,
        }).element,
        title: location.name,
      });

      marker.addListener('click', async () => {
        const locations = await firstValueFrom(this.results$.pipe(
          map(result => result.locations),
        ));
        const foundLocation = locations.find((currentLocation) => currentLocation.id === location.id);
        if (foundLocation) {
          this.selectLocation(foundLocation);
        }
      });

      this.markerCache.set(location.id, marker);
    }

    if (!this.markerCluster) {
      this.markerCluster = new MarkerClusterer({
        // TODO: for performance issues see https://github.com/googlemaps/js-markerclusterer/pull/570
        algorithm: new SuperClusterAlgorithm({
          minZoom: 0,
          maxZoom: MAX_ZOOM - 3,
        }),
        map: googleMap,
        markers: Array.from(this.markerCache.values()),
        renderer: {
          render: ({ count, position }: Cluster) => new AdvancedMarkerElement({
            content: this.createClusterMarkerElement(formatNumber(count, this.localeId)),
            position,
            // adjust zIndex to be above other markers
            zIndex: Number(google.maps.Marker.MAX_ZINDEX) + count,
          }),
        },
      });
    } else {
      const removedMarkers: google.maps.marker.AdvancedMarkerElement[] = [];
      for (const entry of this.markerCache) {
        if (!locations.find((location) => location.id === entry[0])) {
          removedMarkers.push(entry[1]);
          this.markerCache.delete(entry[0]);
        }
      }

      this.markerCluster.removeMarkers(removedMarkers);
      this.markerCluster.addMarkers(Array.from(this.markerCache.values()));
    }
  }

  protected filterToVisibleLocations = false;
  private filterToVisibleLocations$ = new BehaviorSubject(false);
  protected filterToVisibleLocationsChanged(filter: boolean): void {
    this.filterToVisibleLocations$.next(filter);
  }

}
